import React, { useCallback } from 'react';
import { QModalActions } from '../../../QComponents';
import { QButtonGroup, QButton, QIconButton } from '../../../QAtoms';
import { FilterDefinitions } from './types';
import { useFilteringForm } from './useForm';
import { FormProvider } from 'react-hook-form';
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';

export type FilterPopoverProps = {
  children: React.ReactNode;
};

export const FilterPopover = <T extends FilterDefinitions>({
  children,
}: FilterPopoverProps): React.ReactElement => {
  const methods = useFilteringForm<T>();
  const { isOpen, onToggle, onClose } = useDisclosure({
    onOpen: methods.reset,
  });

  const clearAllFilters = useCallback(() => {
    for (const key in methods.getValues()) {
      methods.setValue(key, null);
    }
  }, [methods.getValues, methods.setValue]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onClose)}>
        <Popover
          isOpen={isOpen}
          onClose={onClose}
          placement="bottom-start"
          closeOnEsc
          closeOnBlur
          autoFocus
        >
          {/* @ts-expect-error TEMPORARY: Will be resolved when upgrading to Chakra UI v2 */}
          <PopoverTrigger>
            <QIconButton
              aria-label="Open filter modal"
              iconName="Filter"
              variant="outline"
              onClick={onToggle}
            />
          </PopoverTrigger>
          <PopoverContent
            minWidth="320px"
            width="fit-content"
            _focus={{ boxShadow: 'none' }}
          >
            <PopoverArrow />
            <PopoverBody>{children}</PopoverBody>
            <PopoverFooter>
              <QModalActions>
                <QButtonGroup>
                  <QButton
                    data-cy="clear-filters-button"
                    variant="ghost"
                    onClick={clearAllFilters}
                  >
                    Clear
                  </QButton>
                </QButtonGroup>
                <QButton data-cy="apply-filters-button" type="submit">
                  Apply filters
                </QButton>
              </QModalActions>
            </PopoverFooter>
          </PopoverContent>
        </Popover>
      </form>
    </FormProvider>
  );
};
FilterPopover.DisplayName = 'FilterPopover';
